import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {jwtDecode} from 'jwt-decode';
import katex from 'katex';
import 'katex/dist/katex.min.css';

export const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";

    let password = "";
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }

    return password;
}

export const checkAuthenticatedUser = async () => {
    try {
        const isAuth = localStorage?.getItem('token');

        if (isAuth) {
            const authUser = jwtDecode(isAuth);
            const now = Date.now();
            if (now < authUser.expiration) {
                return true
            } else {
                localStorage?.removeItem('token');
                throw new Error("Invalid Session")
            }
        }
        localStorage?.removeItem('token');
        throw new Error("Invalid token specified")
    } catch (error) {
        localStorage?.removeItem('token');
        throw new Error("Someth happened, try again")
    }

}

export const checkAuthenticatedAdmin = async () => {
    try {
        const isAdminAuth = sessionStorage?.getItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
        const authAdmin = jwtDecode(isAdminAuth);
        const now = Date.now();
        if (isAdminAuth && now < authAdmin.expiration) {
            return true
        } else {
            sessionStorage?.removeItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
            throw new Error('Session Expired');
        }
    } catch (error) {
        sessionStorage?.removeItem('afd8TvhsdjwiuuvsgjhsAfgsUhjs');
        throw new Error("Invalid Session")
    }
}

//CHECK for EMPTY STRINGS 
export const checkEmptyStringsInObj = (arr, ...exemptKeys) => {
  for (const obj of arr) {
    for (const [key, value] of Object.entries(obj)) {
      if (value === "" && !exemptKeys.includes(key)) {
        return false;
      }
    }
  }
  return true;
};


export const checkEmptyStringsInObjNoExempt = (arr) => {
    for (let i = 0; i < arr.length; i++) {
      const obj = arr[i];
      for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] === '') {
          return false;
        }
      }
    }
    return true;
}
  

export const checkEmptyStrings = (arr) => {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] === '') {
        return false;
      }
    }
    return true;
}


export const getOrdinalDate = () => {
    const date = new Date();
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();

    // Function to add ordinal suffix to date
    function getOrdinalSuffix(n) {
        if (n > 3 && n < 21) return 'th'; // handles special case for numbers between 11 and 13
        switch (n % 10) {
            case 1:  return "st";
            case 2:  return "nd";
            case 3:  return "rd";
            default: return "th";
        }
    }

    return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
}
  
export const isIOSStandalonePWA = () => {
    // Check if iOS (iPhone/iPad/iPod)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    // Check if in standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone || 
                        document.referrer.includes('android-app://');
  
    return isIOS && isStandalone;
}
  

// Helper to get and cache backend price data
const getUserPriceData = async () => {
    const res = await axios.get('/api/user-price');
    return res.data;
};

export const fetchPrice = async (category, usage) => {
    const data = await getUserPriceData();
    return data.prices?.[category]?.[usage] || 0;
};

export const fetchCurrency = async () => {
    const data = await getUserPriceData();
    return data.currency || 'USD';
};

export const fetchCountryData = async () => {
    const data = await getUserPriceData();
    return {
        country: data.country,
        continent: data.continent,
        currency: data.currency
    };
};

export const fetchIp = async () => {
    const data = await getUserPriceData();
    return data.ip || '';
};

export const getMonthShortName = (dateString) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
  
    const date = new Date(dateString);
    const monthIndex = date.getMonth();
    const monthShortName = months[monthIndex];
  
    return monthShortName;
}

export const capitalizeWords = (sentence) => {
    if (sentence == null) return '';
    const str = typeof sentence === 'string' ? sentence : String(sentence);
    if (!str) return '';
    return str
        .split(' ')
        .filter(Boolean)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    const pad = (num) => String(num).padStart(2, '0');
    const screenWidth = window.innerWidth
    if (screenWidth < 700 && hrs === 0) {
        return `${pad(mins)}:${pad(secs)}`;
    }
    return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
}


// Converts "hh:mm:ss" or seconds to total seconds
function parseTime(input) {
    if (typeof input === "number") return input;
    const parts = input.split(":").map(Number);
    if (parts.length === 3) {
        return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
        return parts[0] * 60 + parts[1];
    }
    return Number(input) || 0;
}


// React hook for countdown
export function useCountdown(initialTime) {
    const [seconds, setSeconds] = useState(() => parseTime(initialTime));
    const intervalRef = useRef();
    const endTimeRef = useRef();

    useEffect(() => {
        const totalSeconds = parseTime(initialTime);
        setSeconds(totalSeconds);

        // compute a fixed end timestamp so the remaining time is derived
        // from wall-clock time (Date.now) instead of relying on interval ticks.
        endTimeRef.current = Date.now() + totalSeconds * 1000;

        if (intervalRef.current) clearInterval(intervalRef.current);

        const tick = () => {
            const remaining = Math.max(0, Math.round((endTimeRef.current - Date.now()) / 1000));
            setSeconds(remaining);
            if (remaining <= 0 && intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };

        // run first tick immediately to avoid 1s delay
        tick();
        intervalRef.current = setInterval(tick, 1000);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [initialTime]);

    return formatTime(seconds);
}

/**
 * Lightweight markdown-to-JSX renderer for AI-generated study notes.
 * Handles: headings (#/##/###), bold (**text**), italic (*text*),
 * unordered lists (- / *), ordered lists (1.), LaTeX display blocks and
 * inline math, horizontal rules, and ordinary paragraphs.
 * Renders LaTeX display blocks (\[...\]) and inline math (\(...\)) using KaTeX.
 */
export const renderMarkdownNote = (text) => {
    if (text == null) return '';
    if (typeof text !== 'string') return text;

    // Render a LaTeX expression with KaTeX, falling back to the raw expression on error
    const renderKatex = (expr, displayMode, key) => {
        try {
            const html = katex.renderToString(expr.trim(), {
                displayMode,
                throwOnError: false,
                strict: false,
            });
            return (
                <span
                    key={key}
                    style={displayMode ? { display: 'block', overflowX: 'auto', margin: '6px 0' } : undefined}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            );
        } catch {
            return <code key={key} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4, fontSize: '0.88em' }}>{expr.trim()}</code>;
        }
    };

    // Inline renderer: bold, italic, inline code, and inline LaTeX \(...\)
    const renderInline = (line, keyBase) => {
        const parts = [];
        // Match **bold**, *italic*, `code`, \(...\) inline math
        const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|\\\((.+?)\\\))/g;
        let last = 0, idx = 0, m;
        re.lastIndex = 0;
        while ((m = re.exec(line)) !== null) {
            if (m.index > last) parts.push(line.slice(last, m.index));
            if (m[2] != null) parts.push(<strong key={idx++}>{m[2]}</strong>);
            else if (m[3] != null) parts.push(<em key={idx++}>{m[3]}</em>);
            else if (m[4] != null) parts.push(<code key={idx++} style={{ background: '#f1f5f9', padding: '1px 5px', borderRadius: 4, fontSize: '0.88em' }}>{m[4]}</code>);
            else if (m[5] != null) parts.push(renderKatex(m[5], false, `km-${keyBase}-${idx++}`));
            last = m.index + m[0].length;
        }
        if (last < line.length) parts.push(line.slice(last));
        return parts.length ? parts : line;
    };

    // Split source into segments: display-math blocks (\[...\]) and everything else
    const segments = [];
    const displayMathRe = /\\\[[\s\S]*?\\\]/g;
    let lastIdx = 0, dm;
    displayMathRe.lastIndex = 0;
    while ((dm = displayMathRe.exec(text)) !== null) {
        if (dm.index > lastIdx) segments.push({ type: 'text', src: text.slice(lastIdx, dm.index) });
        segments.push({ type: 'display-math', src: dm[0].slice(2, -2) }); // strip \[ and \]
        lastIdx = dm.index + dm[0].length;
    }
    if (lastIdx < text.length) segments.push({ type: 'text', src: text.slice(lastIdx) });

    const nodes = [];
    let nodeKey = 0;

    const processTextSegment = (src) => {
        const lines = src.split('\n');
        let i = 0;
        while (i < lines.length) {
            const line = lines[i];
            const trimmed = line.trim();

            if (!trimmed) { i++; continue; }

            if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) {
                nodes.push(<hr key={nodeKey++} style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '12px 0' }} />);
                i++; continue;
            }

            const h3m = trimmed.match(/^###\s+(.*)/);
            const h2m = trimmed.match(/^##\s+(.*)/);
            const h1m = trimmed.match(/^#\s+(.*)/);
            if (h1m) { nodes.push(<h3 key={nodeKey++} style={{ fontSize: '1rem', fontWeight: 800, color: '#0c1e28', margin: '18px 0 6px', lineHeight: 1.3 }}>{renderInline(h1m[1], nodeKey)}</h3>); i++; continue; }
            if (h2m) { nodes.push(<h4 key={nodeKey++} style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1a4a50', margin: '14px 0 5px', lineHeight: 1.3 }}>{renderInline(h2m[1], nodeKey)}</h4>); i++; continue; }
            if (h3m) { nodes.push(<h5 key={nodeKey++} style={{ fontSize: '0.87rem', fontWeight: 700, color: '#2e6b71', margin: '10px 0 4px', lineHeight: 1.3 }}>{renderInline(h3m[1], nodeKey)}</h5>); i++; continue; }

            if (/^[-*+]\s/.test(trimmed)) {
                const items = [];
                while (i < lines.length && /^[-*+]\s/.test(lines[i].trim())) {
                    items.push(<li key={i} style={{ marginBottom: 4, lineHeight: 1.6 }}>{renderInline(lines[i].trim().replace(/^[-*+]\s/, ''), i)}</li>);
                    i++;
                }
                nodes.push(<ul key={nodeKey++} style={{ margin: '6px 0 8px', paddingLeft: 20, listStyleType: 'disc' }}>{items}</ul>);
                continue;
            }

            if (/^\d+\.\s/.test(trimmed)) {
                const items = [];
                while (i < lines.length && /^\d+\.\s/.test(lines[i].trim())) {
                    items.push(<li key={i} style={{ marginBottom: 4, lineHeight: 1.6 }}>{renderInline(lines[i].trim().replace(/^\d+\.\s/, ''), i)}</li>);
                    i++;
                }
                nodes.push(<ol key={nodeKey++} style={{ margin: '6px 0 8px', paddingLeft: 20 }}>{items}</ol>);
                continue;
            }

            const paraLines = [];
            while (
                i < lines.length &&
                lines[i].trim() &&
                !/^#{1,3}\s/.test(lines[i].trim()) &&
                !/^[-*+]\s/.test(lines[i].trim()) &&
                !/^\d+\.\s/.test(lines[i].trim()) &&
                !/^(-{3,}|\*{3,}|_{3,})$/.test(lines[i].trim())
            ) {
                paraLines.push(lines[i]);
                i++;
            }
            if (paraLines.length) {
                nodes.push(
                    <p key={nodeKey++} style={{ margin: '0 0 8px', lineHeight: 1.7, color: '#263238' }}>
                        {renderInline(paraLines.join('\n'), nodeKey)}
                    </p>
                );
            }
        }
    };

    for (const seg of segments) {
        if (seg.type === 'display-math') {
            nodes.push(renderKatex(seg.src, true, `dm-${nodeKey++}`));
        } else {
            processTextSegment(seg.src);
        }
    }

    return nodes.length ? nodes : text;
};

export const formatLargeText = (text) => {
    if (text == null) return '';
    if (typeof text !== 'string') return text;

    // If the text contains HTML tags, parse it and construct React nodes preserving
    // basic structure (paragraphs, bold, lists, line breaks) while stripping
    // interactive controls like buttons/icons that LinkedIn injects.
    const containsHtml = /<[a-z][\s\S]*>/i.test(text);
    if (!containsHtml) {
        // Fallback: existing simple plaintext handling (preserve paragraphs & bold markers)
        const paragraphs = text.split(/\n{2,}/);
        return paragraphs.map((para, pIdx) => {
            const lines = para.split('\n');
            return (
                <p key={pIdx} style={{ margin: '0.5rem 0', lineHeight: '1.5' }}>
                    {lines.map((line, lIdx) => {
                        const parts = [];
                        let lastIndex = 0;
                        const boldRegex = /\*\*(.+?)\*\*/g;
                        let match;
                        let idx = 0;
                        while ((match = boldRegex.exec(line)) !== null) {
                            if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
                            parts.push(<strong key={idx++}>{match[1]}</strong>);
                            lastIndex = boldRegex.lastIndex;
                        }
                        if (lastIndex < line.length) parts.push(line.slice(lastIndex));
                        if (parts.length === 0) parts.push(line);
                        return (
                            <React.Fragment key={lIdx}>
                                {parts}
                                {lIdx < lines.length - 1 && <br />}
                            </React.Fragment>
                        );
                    })}
                </p>
            );
        });
    }

    // Browser DOM parser
    let parser;
    try {
        parser = new DOMParser();
    } catch (e) {
        // Not in browser environment; fallback to stripping tags
        const stripped = text.replace(/<[^>]+>/g, '\n');
        return formatLargeText(stripped);
    }

    const doc = parser.parseFromString(text, 'text/html');

    // Remove interactive controls and hidden markup that are not part of the description text
    const removeSelectors = [
        'button',
        'icon',
        'script',
        'style',
        '.show-more-less-html__button',
        '.show-more-less-button',
        '.show-more-less-html__button-icon'
    ];
    for (const sel of removeSelectors) {
        try {
            doc.querySelectorAll(sel).forEach(n => n.remove());
        } catch (e) {}
    }

    // Helper: convert a DOM node to React nodes recursively
    let keyCounter = 0;
    const nodeToReact = (node) => {
        if (!node) return null;
        const k = keyCounter++;
        if (node.nodeType === Node.TEXT_NODE) {
            // collapse multiple spaces/newlines into single spaces where appropriate
            return node.textContent.replace(/\s+/g, ' ');
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return null;
        const tag = node.tagName.toLowerCase();
        const children = Array.from(node.childNodes).map((c, i) => <React.Fragment key={`${k}-${i}`}>{nodeToReact(c)}</React.Fragment>);

        switch (tag) {
            case 'br':
                return <br key={k} />;
            case 'strong':
            case 'b':
                return <strong key={k}>{children}</strong>;
            case 'em':
            case 'i':
                return <em key={k}>{children}</em>;
            case 'li':
                return <li key={k}>{children}</li>;
            case 'ul':
            case 'ol':
                return React.createElement(tag, { key: k, style: { margin: '0.5rem 0' } }, children);
            case 'p':
            case 'div':
            case 'section':
                // Some LinkedIn fragments use <div> for paragraphs; render as <p>
                // But if the div contains only a list, let the list render itself
                if (node.querySelector && node.querySelector('li')) {
                    return <div key={k}>{children}</div>;
                }
                return <p key={k} style={{ margin: '0.5rem 0', lineHeight: '1.5' }}>{children}</p>;
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
                return <strong key={k} style={{ display: 'block', margin: '0.5rem 0' }}>{children}</strong>;
            default:
                // Generic container: return children only
                return <React.Fragment key={k}>{children}</React.Fragment>;
        }
    };

    // Find the main content container if present (LinkedIn uses .show-more-less-html__markup)
    const main = doc.querySelector('.show-more-less-html__markup') || doc.body || doc;
    const outNodes = [];
    Array.from(main.childNodes).forEach((n) => {
        const v = nodeToReact(n);
        if (v === null) return;
        // Flatten text nodes containing only whitespace
        if (typeof v === 'string' && !v.trim()) return;
        outNodes.push(v);
    });

    // If nothing produced, fall back to stripped text
    if (outNodes.length === 0) return <p style={{ margin: '0.5rem 0' }}>{doc.body.textContent}</p>;
    return outNodes;
}

export const capitalizeAllLetters = (str) => {
    return str.replace(/[a-zA-Z]/g, (char) => char.toUpperCase());
}

// Robust clipboard copy helper (works in iOS PWAs via textarea fallback)
export const copyText = async (text) => {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        // Try modern API first
        await navigator.clipboard.writeText(text);
        return true;
    }
    // Fallback for iOS Safari/PWA and older browsers
    return new Promise((resolve, reject) => {
        try {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.top = '-1000px';
            textarea.style.left = '-1000px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.focus();
            textarea.select();
            textarea.setSelectionRange(0, textarea.value.length);
            const ok = document.execCommand && document.execCommand('copy');
            document.body.removeChild(textarea);
            if (ok) return resolve(true);
            return reject(new Error('execCommand copy failed'));
        } catch (err) {
            return reject(err);
        }
    });
}

// Parse a user-entered email string into an array of cleaned emails and validate.
// Accept separators: comma, semicolon, whitespace, pipe, slash, backslash.
// Also tolerate a trailing punctuation (like a trailing dot when pasted after a sentence).
export const parseAndValidateEmails = (input) => {
    if (!input || typeof input !== 'string') return { valid: [], invalid: [] };
    // Split on common separators but NOT on dot (.) to avoid breaking email local/host parts
    const parts = input.split(/[,;|\s\\/]+/).map(p => p.trim()).filter(Boolean);
    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
    const valid = [];
    const invalid = [];
    for (let p of parts) {
        // Trim wrapping/trailing punctuation that may appear when copying sentences
        // Remove leading/trailing punctuation that may appear when copying from sentences
        p = p.replace(/^[\s.,;:()<>"']+/, '').replace(/[\s.,;:()<>"']+$/, '');
        if (!p) continue;
        // Defensive split if someone accidentally included multiple emails without separators
        const subParts = p.split(/,|;|\s+/).map(s => s.trim()).filter(Boolean);
        for (const s of subParts) {
            if (basicEmailRegex.test(s)) valid.push(s);
            else invalid.push(s);
        }
    }
    // Deduplicate preserving order
    const seen = new Set();
    const uniqValid = [];
    for (const e of valid) {
        const lower = String(e).toLowerCase();
        if (!seen.has(lower)) { seen.add(lower); uniqValid.push(e); }
    }
    return { valid: uniqValid, invalid };
};

// Shared starter to call backend /user/auto-apply/start. Accepts selectedJobIds (array),
// manualEmailsMap (object mapping jobId -> array of email strings), forceSend boolean.
export const startAutoApply = async ({ selectedJobIds, manualEmailsMap, forceSend = false, tokenOverride = null }) => {
    const token = tokenOverride || localStorage.getItem('token');
    const payload = { selectedJobIds };
    if (manualEmailsMap && typeof manualEmailsMap === 'object') payload.manualEmails = manualEmailsMap;
    if (forceSend) payload.forceSend = true;
    try {
        const resp = await axios.post('/user/auto-apply/start', payload, { headers: { 'x-access-token': token } });
        return resp && resp.data ? resp.data : null;
    } catch (err) {
        try {
            console.error('[startAutoApply] error', {
                message: err?.message,
                status: err?.response?.status,
                data: err?.response?.data
            });
        } catch (_) {}
        throw err;
    }
};

// Start an exam session: POST to /mock/start-exam then open SSE to /mock/exam-progress/{jobId}
export const startExamSession = async ({
    payload,
    endpoint = '/mock/start-exam',
    tokenOverride = null,
    onOngoing = () => {},
    onDone = () => {},
    onProgress = () => {},
    onError = () => {},
    onJobCreated = () => {},
    setLoading = null,
    setProgressStatus = null,
    setProgressPercentage = null,
    sseUrlBase = '/mock/exam-progress'
}) => {
    const token = tokenOverride || localStorage.getItem('token');
    try {
        const resp = await axios.post(endpoint, payload, { headers: { 'x-access-token': token } });
        const data = resp?.data || {};

        if (data.status === 'ongoing') {
            try { onOngoing(data.examDetails); } catch (_) {}
            return { status: 'ongoing' };
        }

        if (!data.jobId) {
            const msg = data.error || 'Failed to start exam. Please try again.';
            try { onError(msg); } catch (_) {}
            return { status: 'error', error: msg };
        }

        const jobId = data.jobId;
        try { onJobCreated(jobId); } catch (_) {}

        const sseUrl = `${sseUrlBase}/${jobId}`;
        const evtSource = new EventSource(sseUrl);

        evtSource.onerror = (err) => {
            if (setLoading) try { setLoading(false); } catch (_) {}
            try { onError('Connection lost. Please try again.'); } catch (_) {}
            try { evtSource.close(); } catch (_) {}
        };

        evtSource.addEventListener('progress', (e) => {
            try {
                const { status, progress } = JSON.parse(e.data);
                if (setProgressStatus) try { setProgressStatus(status || 'Processing...'); } catch (_) {}
                if (setProgressPercentage) try { setProgressPercentage(progress || 10); } catch (_) {}
                try { onProgress(status, progress); } catch (_) {}
            } catch (ex) {
                if (setProgressStatus) try { setProgressStatus('Processing...'); } catch (_) {}
            }
        });

        evtSource.addEventListener('done', (e) => {
            try {
                try { onDone(e); } catch (_) {}
            } finally {
                if (setLoading) try { setLoading(false); } catch (_) {}
                try { evtSource.close(); } catch (_) {}
            }
        });

        evtSource.addEventListener('error', (e) => {
            if (setLoading) try { setLoading(false); } catch (_) {}
            try {
                const parsed = e.data ? JSON.parse(e.data) : {};
                onError(parsed.error || 'An error occurred. Please try again.');
            } catch (ex) {
                try { onError('An error occurred. Please try again.'); } catch (_) {}
            }
            try { evtSource.close(); } catch (_) {}
        });

        return { status: 'started', jobId, evtSource };
    } catch (err) {
        if (err?.response?.data?.openPricing) {
            window.dispatchEvent(new CustomEvent('openExamPricing'));
        }
        const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to start exam. Please try again.';
        if (setLoading) try { setLoading(false); } catch (_) {}
        try { onError(msg); } catch (_) {}
        throw err;
    }
};


